import {
  LossRunsButton,
  LossRunsFrom,
  LossRunsPill,
} from "@/components/submissions/lossRuns/LossRunsHeader";
import { BusinessStep } from "@/components/submissions/wc/BusinessStep";
import { CarrierQuestionsStep } from "@/components/submissions/wc/CarrierQuestionsStep";
import { HistoryStep } from "@/components/submissions/wc/HistoryStep";
import { LocationsStep } from "@/components/submissions/wc/LocationsStep";
import { PeopleStep } from "@/components/submissions/wc/PeopleStep";
import { QuestionsStep } from "@/components/submissions/wc/QuestionsStep";
import { WC_STEP_ICONS } from "@/components/submissions/wc/stepIcons";
import {
  defineWizard,
  ROOT,
  type WizardStep,
  type WizardSubstep,
} from "@/base";
import { localTodayIso } from "@/lib/dates";
import { formatIsoDate } from "@/lib/displayFormat";
import { initialsOf } from "@/lib/initials";
import { formatShortDate } from "@/lib/listFormat";
import {
  listSubmissionQuotes,
  submitSubmission,
} from "@/lib/submissions/brokerSubmissions";
import {
  moreQuestionsRefusal,
  validateCarrierStep,
} from "@/lib/submissions/wc/carrierStep";
import {
  BUSINESS_REFUSALS,
  insuredEditable,
  startDateField,
  validateBusinessStep,
} from "@/lib/submissions/wc/business";
import {
  prefillAnswers,
  prefillBusiness,
  prefillLocations,
  wcFromProposal,
} from "@/lib/submissions/wc/fromProposal";
import {
  createWcApplication,
  emptyWcDraft,
  loadWcDraft,
  saveWcBusiness,
  saveWcCarrierAnswers,
  saveWcHistory,
  saveWcLocations,
  saveWcPeople,
  saveWcQuestions,
  type WcDraft,
} from "@/lib/submissions/wc/draft";
import {
  businessAge,
  HISTORY_REFUSALS,
  historyComplete,
  validateHistory,
} from "@/lib/submissions/wc/history";
import {
  PEOPLE_REFUSALS,
  payrollLocations,
  validatePeopleStep,
} from "@/lib/submissions/wc/peopleStep";
import {
  LOCATIONS_REFUSALS,
  locationsDone,
  validateLocations,
} from "@/lib/submissions/wc/locations";
import {
  answersComplete,
  BASIC_INFORMATION,
  COVERAGE,
  GENERAL_INFORMATION,
  HISTORY,
  SCHEDULE_RATING,
  validateAnswers,
  type QuestionSection,
} from "@/lib/submissions/wc/questions";
import { validateCoverage } from "@/lib/submissions/wc/coverage";
import {
  quotesNotice,
  submitRefusal,
  WC_BASE_PATH,
  WC_LAST_STEP,
  WC_LAST_STEP_TITLE,
  WC_PAGE_TITLES,
  WC_STEP_TITLES,
  type SubmittedState,
} from "@/lib/submissions/wc/review";

// The Workers' Comp wizard's definition (BRK-E020, restructured by BRK-S387):
// 1. Business: Basic information (BRK-S301), then Characteristics & History
//    (BRK-S389): current coverage, a card a year of prior coverage, claims;
// 2. Locations (BRK-S302);
// 3. People (BRK-S303);
// 4. Questions and coverage (BRK-S304, BRK-S392): Coverage with the
//    employers' liability limits as one choice, then General information
//    (ACORD 130) with its usual answers chosen and a confirmation, then
//    Schedule rating;
// 5. Carrier-specific questions (BRK-S393): the application goes to every
//    carrier at once as it opens, each shows its indication and asks what
//    the application can't answer; Submit saves the answers, then sends it.
// There is no Review step, and no panel of what the markets said: the old
// Review URL opens the last step. Deployed builds keep today's flow until the
// wizard is switched on (VITE_ENABLE_WC_WIZARD).
//
// Going back and correcting (BRK-S307): each step saves only its own part, so
// an earlier step's change keeps every later answer. What a change affects is
// declared where the change is made:
// - a location's state change re-checks each of its class codes, and clears,
//   with a note, one the new state's bureau doesn't offer (ClassCodeField,
//   BRK-S291);
// - removing a location that has class codes asks first, and its payroll
//   rows go with it (LocationsStep, `saveWcLocations`);
// A later step that a change leaves incomplete reads it from PMS (each
// step's `isComplete`), so the stepper says it needs attention and a resumed
// application lands there. After each save the wizard reads the quotes, and
// says they need updating only when PMS marked one outdated (PMS-S707).
//
// An uploaded ACORD packet (BRK-S306) fills Basic information,
// Characteristics & History's answers, Locations and each Questions section,
// each as its page opens (`wcFromProposal`); People waits for extraction to
// say who is who.

/** Basic information's checks (BRK-S388): the insured's and the effective
 *  date, then what the manifest declares there (the start date, the
 *  section's fields such as the years of experience). */
function businessErrors(d: WcDraft) {
  return {
    ...validateBusinessStep(
      d.business,
      insuredEditable(d.account, d.submission),
      localTodayIso(),
      { askStartDate: startDateField(d.manifest) !== null },
    ),
    ...validateAnswers(d.manifest, d.answers, BASIC_INFORMATION),
  };
}

const basic: WizardSubstep<WcDraft> = {
  id: "basic",
  title: WC_PAGE_TITLES["business/basic"],
  description: "The insured’s business, and when the policy starts.",
  render: (ctx) => <BusinessStep {...ctx} />,
  isComplete: (d) =>
    d.account !== null &&
    d.submission !== null &&
    Object.keys(businessErrors(d)).length === 0,
  validate: businessErrors,
  save: ({ request, id }, d) => saveWcBusiness(request, id, d),
  refusals: BUSINESS_REFUSALS,
};

/** Characteristics & History (BRK-S389): current coverage, the years of
 *  prior coverage a card a year, and the claims. The reasons follow the
 *  business's age from its start date. */
const history: WizardSubstep<WcDraft> = {
  id: "history",
  title: WC_PAGE_TITLES["business/history"],
  description:
    "The business’s workers’ comp history: its coverage now and before, and its claims.",
  render: (ctx) => <HistoryStep {...ctx} />,
  // Never done on a catalog that couldn't be read: its questions are unknown.
  isComplete: (d) =>
    d.submission !== null &&
    d.manifest !== null &&
    historyComplete(d.manifest, d.submission),
  validate: (d) =>
    d.manifest === null
      ? { [ROOT]: "The questions are still loading." }
      : validateHistory(d.manifest, d.answers, d.coverageYears, {
          carriers: d.priorCarriers,
          age: businessAge(d.business.businessStartDate, localTodayIso()),
        }),
  save: ({ request, id }, d) => saveWcHistory(request, id, d),
  refusals: HISTORY_REFUSALS,
};

const business: WizardStep<WcDraft> = {
  id: "business",
  title: WC_STEP_TITLES.business,
  icon: WC_STEP_ICONS.business,
  substeps: [basic, history],
};

const locations: WizardStep<WcDraft> = {
  id: "locations",
  title: WC_STEP_TITLES.locations,
  icon: WC_STEP_ICONS.locations,
  description:
    "Where the business works, and each location’s class codes, employees and payroll. They’re kept on this submission: the insured’s record gets them only when the policy binds.",
  render: (ctx) => <LocationsStep {...ctx} buildings={false} />,
  isComplete: (d) => d.submission !== null && locationsDone(d.submission.data),
  // The mailing address is checked only while the step may write it, and
  // building details never: Workers' Comp doesn't ask them (BRK-S390).
  validate: (d) =>
    d.savedLocations === null
      ? { [ROOT]: "The locations are still loading." }
      : validateLocations(
          d.locations,
          insuredEditable(d.account, d.submission) ? d.mailing : null,
          { buildings: false },
        ),
  save: ({ request, id }, d) => saveWcLocations(request, id, d),
  refusals: LOCATIONS_REFUSALS,
};

const people: WizardStep<WcDraft> = {
  id: "people",
  title: WC_STEP_TITLES.people,
  icon: WC_STEP_ICONS.people,
  description:
    "The business’s owners and officers, whether the policy covers them, and who the carrier can call. They’re kept on this submission: the insured’s record gets them only when the policy binds.",
  render: (ctx) => <PeopleStep {...ctx} />,
  // Saving the step writes `includeOfficers`; PMS's `owners` floor key holds
  // the at-least-one rule at Submit (BRK-S391).
  isComplete: (d) => typeof d.submission?.data.includeOfficers === "boolean",
  validate: (d) =>
    d.personnelUnavailable
      ? { [ROOT]: "This PMS doesn’t keep a submission’s people yet." }
      : d.personnel === null
        ? { [ROOT]: "The people are still loading." }
        : d.savedLocations === null
          ? { [ROOT]: "The locations are still loading." }
          : validatePeopleStep(
              d.people,
              d.contacts,
              d.personnel,
              payrollLocations(d.savedLocations, d.submission?.data ?? {}).map(
                (l) => l.id,
              ),
            ),
  save: ({ request, id }, d) => saveWcPeople(request, id, d),
  refusals: PEOPLE_REFUSALS,
};

/** One section of the questions as its own sub-step (BRK-S387): it checks
 *  and saves that section alone. */
function questionsPage(
  id: string,
  title: string,
  description: string,
  section: QuestionSection,
): WizardSubstep<WcDraft> {
  return {
    id,
    title,
    description,
    render: (ctx) => <QuestionsStep {...ctx} section={section} />,
    isComplete: (d) =>
      d.submission !== null &&
      answersComplete(d.manifest, d.submission.data, section),
    validate: (d) =>
      d.manifest === null
        ? { [ROOT]: "The questions are still loading." }
        : section === COVERAGE
          ? validateCoverage(d.manifest, d.answers)
          : validateAnswers(d.manifest, d.answers, section),
    save: ({ request, id: appId }, d) =>
      saveWcQuestions(request, appId, d, section),
  };
}

const coverage = questionsPage(
  "coverage",
  WC_PAGE_TITLES["questions/coverage"],
  "The coverage the business needs, and whether it was declined before.",
  COVERAGE,
);
const general = questionsPage(
  "general",
  WC_PAGE_TITLES["questions/general"],
  "The standard ACORD 130 questions about the business’s operations, with the usual answers chosen for you to check.",
  GENERAL_INFORMATION,
);
const scheduleRating = questionsPage(
  "schedule-rating",
  WC_PAGE_TITLES["questions/schedule-rating"],
  "How the business runs day to day, for the carrier’s schedule rating.",
  SCHEDULE_RATING,
);

const questions: WizardStep<WcDraft> = {
  id: "questions",
  title: WC_STEP_TITLES.questions,
  icon: WC_STEP_ICONS.questions,
  substeps: [coverage, general, scheduleRating],
};

const carrierQuestions: WizardStep<WcDraft> = {
  id: WC_LAST_STEP,
  title: WC_LAST_STEP_TITLE,
  icon: WC_STEP_ICONS[WC_LAST_STEP],
  // Only what the carriers ask; their indications show with the quotes
  // (BRK-S491).
  description: "What each carrier still needs to know, then Get Quotes.",
  render: (ctx) => <CarrierQuestionsStep {...ctx} />,
  // Done once PMS has taken Get Quotes: a draft until then (PMS-S708).
  isComplete: (d) => d.submission !== null && d.submission.status !== "draft",
  // Every earlier page is done before this one opens; this one checks each
  // carrier's answers, and PMS checks the rest.
  validate: (d) => validateCarrierStep(d.carrierStep),
  // Each carrier's answers, before Get Quotes sends the application.
  save: ({ request, id }, d) => saveWcCarrierAnswers(request, id, d),
};

export const WC_WIZARD = defineWizard<WcDraft>({
  line: "workers_compensation",
  title: "Workers' Comp submission",
  // Whose application it is, its state and its line (BRK-S359), each only
  // once PMS holds it: a new one is "New Workers' Comp application".
  heading: ({ account, submission }) => {
    const insured = submission ? (account?.legalName ?? null) : null;
    return {
      title: insured ?? "New Workers' Comp application",
      initials: insured ? initialsOf(insured) : undefined,
      status: !submission
        ? undefined
        : submission.status === "draft"
          ? "Draft"
          : submission.submittedAt
            ? `Submitted ${formatShortDate(submission.submittedAt)}`
            : "Submitted",
      meta: [
        "Workers' Compensation application",
        submission?.submissionNumber,
        submission?.effectiveDate
          ? `Effective ${formatIsoDate(submission.effectiveDate)}`
          : null,
      ]
        .filter(Boolean)
        .join(" · "),
      // Its loss runs once it exists (BRK-S361), from the page's provider,
      // which takes each save's read of the submission (BRK-S389).
      aside: submission ? <LossRunsPill /> : undefined,
      actions: submission ? (
        <>
          <LossRunsFrom submission={submission} />
          <LossRunsButton />
        </>
      ) : undefined,
    };
  },
  basePath: WC_BASE_PATH,
  steps: [business, locations, people, questions, carrierQuestions],
  formerSteps: { review: WC_LAST_STEP },
  empty: () => emptyWcDraft(),
  load: loadWcDraft,
  create: (request, values) =>
    createWcApplication(request, values.business, values.answers),
  // The last step's answers are saved, then Submit (PMS-S708) records a quote
  // request on each carrier ready for one and calls none (PMS-S815). The
  // submission's page is handed the fan-out the last step ran, so it shows it
  // without asking again, and the carriers whose quote it then requests, all
  // at once (BRK-S393). Missing answers come back as the last step's
  // checklist, and a carrier's questions left land on it. The progress screen
  // names each stage in turn (BRK-S353).
  submitStages: ["Saving your answers", "Sending your application to Signal"],
  // Submit is removed: the one button gets the quotes, and does what Submit
  // did (BRK-S491; Nav, 2026-10-28).
  submitLabel: "Get Quotes",
  submittingLabel: "Getting Quotes…",
  submitProgressTitle: "Getting Your Quotes",
  submit: async ({ request, id, stage }, d): Promise<SubmittedState> => {
    // A round that opened more questions stops here, before PMS is asked for
    // quotes, and shows them (BRK-S497).
    const more = moreQuestionsRefusal(d.carrierStep);
    if (more) throw more;
    stage(1);
    let requestedCarrierIds: string[];
    try {
      ({ requestedCarrierIds } = await submitSubmission(request, id));
    } catch (err) {
      throw submitRefusal(err, d.manifest);
    }
    const carriers = d.carrierStep?.carriers ?? [];
    const legs = carriers.flatMap((c) => (c.indication ? [c.indication] : []));
    return {
      ...(legs.length > 0 ? { indications: { results: legs } } : {}),
      requested: requestedCarrierIds.map((carrierId) => ({
        carrierId,
        carrierName:
          carriers.find((c) => c.carrierId === carrierId)?.carrier ??
          "The carrier",
      })),
    };
  },
  fromProposal: (proposal) => {
    const doc = wcFromProposal(proposal);
    return {
      "business/basic": (d) => prefillBusiness(d, doc),
      "business/history": (d) => prefillAnswers(d, doc, HISTORY),
      [locations.id]: (d) => prefillLocations(d, doc),
      "questions/coverage": (d) => prefillAnswers(d, doc, COVERAGE),
      "questions/general": (d) => prefillAnswers(d, doc, GENERAL_INFORMATION),
    };
  },
  submittedPath: (id) => `/submissions/${id}`,
  // Submitted once PMS moved it off draft, when PMS stamped (PMS-S708).
  submitted: (d) =>
    d.submission && d.submission.status !== "draft"
      ? { at: d.submission.submittedAt ?? null }
      : null,
  noticeAfterSave: async ({ request, id }) =>
    quotesNotice((await listSubmissionQuotes(request, id)).items),
});
